import React from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import cognitesdk from '@cognite/cognitesdk';
import {
  Progress,
  Spin,
  Card,
  notification,
  Button,
  Divider,
  message,
  Select,
  Input,
} from 'antd';

import CogniteViewer from '@cognite/3d-viewer';
import { AssetInfo } from 'components/AssetInfo';

const { Option } = Select;
const { Search } = Input;

const Container = styled.div`
  canvas {
    height: 100vh;
    width: 100%;
    overflow: hidden;
  }
`;
const StyledCard = styled(Card)`
  position: fixed !important;
  bottom: 40px;
  left: 40px;
  max-width: 50%;
  width: 300px;
  display: ${props => (props.visible === 'true' ? 'block' : 'none')};
`;

const ControlSection = styled(Card)`
  position: fixed !important;
  bottom: 40px;
  right: 40px;
  max-width: 50%;
  width: 500px;
`;

const SearchSection = styled(Card)`
  position: fixed !important;
  top: 100px;
  left: 40px;
  max-width: 50%;
  width: 300px;
  display: none;
`;

const AssetDetailsSection = styled(Card)`
  position: absolute !important;
  bottom: 160px;
  top: 100px;
  right: 40px;
  max-width: 50%;
  width: 500px;
  overflow-y: scroll;
  button {
    margin-bottom: 12px;
  }
`;

class Viewer extends React.Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        modelId: PropTypes.string.isRequired,
        revisionId: PropTypes.string.isRequired,
        sceneId: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  };
  state = {
    showLoader: false,
    mounted: true,
    nodeAssetDetails: {
      asset: undefined,
    },
    mode: 'asset',
    control: 'orbit',
  };
  async componentWillMount() {
    cognitesdk.getLoginStatus().then(status => {
      if (!status.loggedIn) {
        cognitesdk.loginWithRedirect();
      }
    });
    const {
      modelId,
      revisionId,
      sceneId,
      projectName,
    } = this.props.match.params;
    this.viewer = new CogniteViewer({
      renderer: {
        downsampleFactor: 1.0,
      },
      apiKey: this.getApiKey(),
      api: '0.4',
      project: projectName,
      modelId,
      revisionId,
      sceneId,
      onClickListener: this.onNodeClickListener,
      onDoubleClickListener: nodeId => {
        notification.open({
          message: 'Asset Double Clicked',
          description: `Node ID: ${nodeId}`,
        });
      },
      progressListener: (step, totalSteps, subprogress, text) => {
        this.setState({
          step,
          totalSteps,
          progress:
            subprogress && Number.isFinite(subprogress)
              ? Math.round(subprogress * 100)
              : 100,
          loadingText: text,
          showLoader: true,
        });
        if (step === totalSteps) {
          setTimeout(() => {
            this.setState({
              showLoader: false,
            });
          }, 2000);
        }
      },
    });
  }

  componentDidMount() {
    this.container.appendChild(this.viewer.canvas);
    this.viewer.start();
  }

  onNodeClickListener = async nodeId => {
    if (this.state.mode === 'asset') {
      try {
        this.setState({ nodeAssetDetails: { asset: null } });
        const { data: nodeAssetDetails } = await this.viewer.getAssetFromNodeId(
          nodeId
        );
        this.highlightNodes(nodeAssetDetails.childIds);
        this.setState({ nodeAssetDetails });
      } catch (e) {
        this.highlightNodes([]);
        this.setState({ nodeAssetDetails: { asset: undefined } });
        message.error('Could not find associated asset');
      }
    } else {
      this.viewer.toggleNodeSelected(nodeId);
    }
  };

  getApiKey = () => {
    if (!localStorage.apiKey || localStorage.apiKey === undefined) {
      return undefined;
    }
    return localStorage.apiKey;
  };

  clearAssetDetails = () => {
    this.viewer.deselectAllNodes();
    this.setState({ nodeAssetDetails: { asset: undefined } });
  };

  highlightNodes = (nodeIds = []) => {
    this.viewer.deselectAllNodes();
    nodeIds.forEach(id => this.viewer.toggleNodeSelected(id));
  };
  changeMount = () => {
    this.setState({ mounted: !this.state.mounted }, async () => {
      if (this.viewer !== undefined) {
        if (!this.state.mounted) {
          this.viewer.stop();
        } else {
          this.viewer.start();
        }
      }
    });
  };

  changeMode = value => {
    this.clearAssetDetails();
    this.setState({ mode: value });
  };

  changeControl = value => {
    this.clearAssetDetails();
    this.setState({ control: value });
    this.viewer.setControlType(value);
  };

  searchByTagId = async value => {
    this.clearAssetDetails();
    try {
      this.setState({ nodeAssetDetails: { asset: null } });
      const { data: nodeAssetDetails } = await this.viewer.getAssetFromTagId(
        value
      );
      this.highlightNodes(nodeAssetDetails.childIds);
      this.setState({ nodeAssetDetails });
    } catch (e) {
      this.highlightNodes([]);
      this.setState({ nodeAssetDetails: { asset: undefined } });
      message.error('Could not find associated asset');
    }
  };

  render() {
    return (
      <Container
        innerRef={container => {
          this.container = container;
        }}
      >
        {this.state.canvas}
        <StyledCard visible={this.state.showLoader.toString()}>
          <Spin
            tip={`Step ${this.state.step} of ${this.state.totalSteps}, ${
              this.state.loadingText
            }`}
          />
          <Progress
            percent={this.state.progress}
            {...(this.state.step !== this.state.totalSteps
              ? { status: 'active' }
              : null)}
          />
        </StyledCard>
        <SearchSection>
          <Search
            placeholder="input search text"
            onSearch={this.searchByTagId}
            style={{ width: 200 }}
          />
        </SearchSection>
        <ControlSection>
          <Button onClick={this.changeMount}>
            {this.state.mounted ? 'UnMount' : 'Mount'}
          </Button>
          <Divider type="vertical" />
          <Select
            defaultValue={this.state.mode}
            onChange={this.changeMode}
            placeholder="Mode"
          >
            <Option value="asset">Asset Picker</Option>
            <Option value="design">Design</Option>
          </Select>
          <Divider type="vertical" />
          <Select
            defaultValue={this.state.control}
            onChange={this.changeControl}
            placeholder="Control Scheme"
            dropdownMatchSelectWidth={false}
          >
            {/* <Option value="fpv">First Person</Option> */}
            <Option value="fpv-no-cursor">First Person</Option>
            <Option value="orbit">Orbit</Option>
          </Select>
        </ControlSection>
        {this.state.nodeAssetDetails.asset !== undefined ? (
          <AssetDetailsSection>
            <Button type="primary" onClick={this.clearAssetDetails}>
              Clear Asset Info
            </Button>
            <AssetInfo asset={this.state.nodeAssetDetails.asset} />
          </AssetDetailsSection>
        ) : null}
      </Container>
    );
  }
}

export default Viewer;



// WEBPACK FOOTER //
// ./src/containers/Viewer/Viewer.js